/**
 * Soft≠Live local cinema fixtures — wall-shaped payload for UX preview.
 * Never sets envelope.actionability = live_preview / LIVE authority.
 */
import { isLocalHost } from "./pathseal.js";

function nearby(id, label, distance_m, reason) {
  return { id, label, distance_m, reason };
}

function evidence(
  snapshot_id, integrity_mark, trigger_kind, trigger_origin, atMs, soft_frame_tone, media_kind, extras = {},
) {
  const origin = String(trigger_origin || "").toLowerCase();
  let kind = media_kind || "";
  if (!kind) {
    if (origin.includes("upload") || origin.includes("personnel")) kind = "upload";
    else if (origin.includes("record") || origin.includes("clip")) kind = "recording";
    else kind = "snapshot";
  }
  return {
    snapshot_id,
    integrity_mark,
    trigger_kind,
    trigger_origin,
    media_kind: kind,
    soft_frame_tone: soft_frame_tone || "dock",
    at: new Date(atMs).toISOString(),
    note: extras.note || "",
    author: extras.author || "",
    author_role: extras.author_role || "",
    ai_read: extras.ai_read || "",
    ai_fixed: !!extras.ai_fixed,
  };
}

function aiAct(id, verb, detail, atMs, result) {
  return {
    id,
    actor: "ai",
    verb,
    detail,
    result: result || "ok",
    at: new Date(atMs).toISOString(),
  };
}

function dispatchLoop({
  id,
  state,
  assignee,
  responder,
  eta_s,
  opened_by,
  opened_lens,
  device_opened,
  device_lens,
  responded_at_ago,
  loop_result,
  after_response,
  dispatch_reason,
  route,
}) {
  const now = Date.now();
  const openedAt = now - (responded_at_ago || 0) - 90000;
  return {
    id,
    state,
    assignee: assignee || "",
    responder: responder || assignee || "",
    eta_s: eta_s == null ? null : eta_s,
    elapsed_s: responded_at_ago != null ? Math.round(responded_at_ago / 1000) : null,
    opened_at: new Date(openedAt).toISOString(),
    responded_at: responded_at_ago != null
      ? new Date(now - responded_at_ago).toISOString()
      : null,
    camera_open: {
      personnel: {
        open: !!opened_by,
        who: opened_by || "",
        lens: opened_lens || "",
        at: opened_by ? new Date(now - (responded_at_ago || 0) - 60000).toISOString() : null,
      },
      device: {
        open: !!device_opened,
        device_id: device_opened || "",
        lens: device_lens || "",
        at: device_opened ? new Date(now - (responded_at_ago || 0) - 45000).toISOString() : null,
      },
    },
    after_response: after_response || "",
    dispatch_reason: dispatch_reason || "",
    loop_result: loop_result || "open",
    route: Array.isArray(route) ? route : [],
    stages: [
      { id: "dispatch", label: "Dispatch", done: true },
      { id: "ack", label: "Ack", done: !!responder },
      { id: "cam_person", label: "Person cam", done: !!opened_by },
      { id: "cam_device", label: "Device cam", done: !!device_opened },
      { id: "result", label: "Close", done: loop_result === "closed" || loop_result === "improving" },
    ],
  };
}

function card({
  source_id,
  label,
  lane,
  tone,
  why,
  control,
  selection,
  dispatch,
  loop,
  nearbyRows,
  response,
  swarm,
  place,
  evidenceRows,
  aiActions,
  pulseAgoMs,
  soft_share,
  group_id,
}) {
  const now = Date.now();
  const loopObj = loop || null;
  return {
    source_id,
    label,
    lane,
    source_kind: lane === "dispatch" ? "personnel" : "device",
    soft_stamp: true,
    soft_neq_live: true,
    soft_reason: "soft_demo_fixture",
    soft_preview_tone: tone || "gate",
    soft_share: soft_share || null,
    is_shared: !!(soft_share && soft_share.by),
    shared_by: soft_share && soft_share.by ? String(soft_share.by) : "",
    live_authority: false,
    envelope: {
      actionability: "soft_preview",
      reason: why,
    },
    session: {
      viewable: false,
      media_played: false,
    },
    binding: {
      device_id: place.device_id || "",
    },
    subject_ref: place.device_id || "",
    zone_id: place.zone_id || "",
    site_id: place.site_id || "",
    group_id: group_id || (swarm && swarm.group_id) || "",
    pulse_at: Math.floor((now - (pulseAgoMs || 0)) / 1000),
    situation: {
      control,
      why,
      selection,
      dispatch: dispatch || (loopObj ? {
        id: loopObj.id,
        state: loopObj.state,
        assignee: loopObj.assignee,
      } : null),
      dispatch_loop: loopObj,
      nearby: nearbyRows || [],
      response: response || { summary: "Soft demo · not Live", posture: "soft" },
      swarm: swarm || { active: false, linked: 0, group_id: "" },
      place: {
        address: place.address || "",
        zone_id: place.zone_id || "",
        site_id: place.site_id || "",
        ll: place.ll || null,
      },
      evidence: evidenceRows || [],
      ai_actions: aiActions || [],
    },
  };
}

/** @returns {{ device_cards: object[], dispatch_cards: object[], activity: object[], count: number, soft_demo: boolean }} */
export function buildSoftCinemaDemoWall() {
  const now = Date.now();
  const sharedLoop = dispatchLoop({
    id: "DSP-SOFT-441",
    state: "en_route",
    assignee: "Unit R-12",
    responder: "Responder Mei",
    eta_s: 240,
    opened_by: "Responder Mei",
    opened_lens: "soft:dispatch:r12",
    device_opened: "CS-8841",
    device_lens: "soft:device:gate-a",
    responded_at_ago: 70000,
    loop_result: "improving",
    dispatch_reason: "Motion + soft trigger at North Gate · Unit R-12 tasked.",
    after_response: "Person cam up; Gate A device cam synced; AI tracking ETA / FOV.",
    route: [
      { id: "wp1", label: "Yard muster", done: true, at_ago_ms: 160000 },
      { id: "wp2", label: "Corridor B", done: true, at_ago_ms: 110000 },
      { id: "wp3", label: "North Gate approach", done: true, at_ago_ms: 50000 },
      { id: "wp4", label: "Gate A · on scene", done: false, at_ago_ms: 0 },
    ],
  });

  const deviceCards = [
    card({
      source_id: "soft:device:gate-a",
      label: "North Gate · Cam A",
      lane: "device",
      tone: "gate",
      why: "Motion + soft trigger from edge skill; wall opened for operator review.",
      control: { kind: "ai", label: "AI watch" },
      selection: "Closest fixed lens to motion centroid + open Soft ticket on Gate A.",
      dispatch: { id: "DSP-SOFT-441", state: "en_route", assignee: "Unit R-12" },
      loop: sharedLoop,
      nearbyRows: [
        nearby("soft:device:gate-b", "North Gate · Cam B", 18, "Sibling FOV"),
        nearby("p-soft-1", "Responder Mei", 120, "En route"),
      ],
      response: { summary: "Good Soft posture · latency projected", posture: "watching" },
      swarm: { active: true, linked: 6, group_id: "grp-gate" },
      place: {
        address: "Lot 12, North Perimeter Road",
        zone_id: "zn-north",
        site_id: "site-soft-1",
        device_id: "CS-8841",
        ll: [3.1582, 101.7124],
      },
      evidenceRows: [
        evidence("ss-1", "SOFT·A1", "ai", "edge_skill", now - 90000, "gate", "snapshot", {
          ai_read: "Edge skill person_near_gate · FOV match Gate A.",
          ai_fixed: true,
        }),
        evidence("ss-2", "SOFT·A2", "ai", "motion_clip", now - 45000, "gate", "recording", {
          ai_read: "Motion clip retained · corridor approach.",
          ai_fixed: true,
        }),
        evidence("ss-up-1", "SOFT·U1", "human", "personnel_upload", now - 20000, "field", "upload", {
          author: "Responder Mei",
          author_role: "responder",
          note: "Approach clear · no forced entry · request device cam FOV.",
          ai_read: "Upload aligns with Gate A FOV · sealed SOFT·U1.",
          ai_fixed: true,
        }),
      ],
      aiActions: [
        aiAct("a1", "detect", "Edge skill flagged person_near_gate", now - 95000, "open_lens"),
        aiAct("a2", "open_device_cam", "Opened device camera CS-8841 / Gate A", now - 88000, "soft_preview"),
        aiAct("a3", "select", "Ranked Gate A as closest FOV to centroid", now - 80000, "selected"),
        aiAct("a4", "dispatch", "Created DSP-SOFT-441 → Unit R-12", now - 72000, "en_route"),
        aiAct("a5", "stamp", "Integrity stamp SOFT·A1", now - 90000, "stored"),
        aiAct("a6", "stamp", "Integrity stamp SOFT·A2", now - 45000, "stored"),
        aiAct("a7", "read_note", "Read responder note · request device FOV", now - 18000, "understood"),
        aiAct("a8", "fix", "Fixed evidence SOFT·U1 after FOV align", now - 12000, "sealed"),
      ],
      pulseAgoMs: 12000,
    }),
    card({
      source_id: "soft:device:yard-w",
      label: "Service Yard · West",
      lane: "device",
      tone: "yard",
      why: "Human takeover from AI after ambiguous silhouette.",
      control: { kind: "human", label: "You · desk" },
      selection: "Operator pinned yard after soft alert cascade.",
      dispatch: null,
      loop: dispatchLoop({
        id: "DSP-SOFT-IDLE",
        state: "standby",
        assignee: "",
        responder: "",
        eta_s: null,
        opened_by: "",
        opened_lens: "",
        device_opened: "CS-2290",
        device_lens: "soft:device:yard-w",
        responded_at_ago: null,
        loop_result: "open",
        after_response: "No responder yet; device cam open under desk takeover.",
      }),
      nearbyRows: [
        nearby("soft:device:gate-a", "North Gate · Cam A", 95, "Perimeter peer"),
      ],
      response: { summary: "Fair · partial FOV Soft demo", posture: "manual" },
      swarm: { active: true, linked: 3, group_id: "grp-yard" },
      soft_share: { by: "partner.admin" },
      group_id: "grp-yard",
      place: {
        address: "Service Yard West apron",
        zone_id: "zn-yard",
        site_id: "site-soft-1",
        device_id: "CS-2290",
        ll: [3.1569, 101.7098],
      },
      evidenceRows: [
        evidence("ss-3", "SOFT·Y1", "human", "desk_takeover", now - 30000, "yard", "snapshot", {
          author: "Desk",
          author_role: "operator",
          note: "Ambiguous silhouette · desk takeover.",
          ai_read: "Hand-off understood · human control held.",
          ai_fixed: false,
        }),
      ],
      aiActions: [
        aiAct("y1", "detect", "Ambiguous silhouette in yard FOV", now - 55000, "escalate"),
        aiAct("y2", "hand_to_human", "Transferred control to desk operator", now - 40000, "human"),
        aiAct("y3", "open_device_cam", "Kept device cam CS-2290 open under human", now - 38000, "soft_preview"),
      ],
      pulseAgoMs: 8000,
    }),
    card({
      source_id: "soft:device:dock-3",
      label: "Loading Dock 3",
      lane: "device",
      tone: "dock",
      why: "Group card automation soft-opened dock lens overnight.",
      control: { kind: "ai", label: "Night watch" },
      selection: "Scheduled Soft open · no Live ticket.",
      dispatch: null,
      loop: null,
      nearbyRows: [],
      response: { summary: "Soft mosaic filler", posture: "schedule" },
      swarm: { active: false, linked: 0, group_id: "grp-dock" },
      place: {
        address: "Loading Dock 3 · Bay C",
        zone_id: "zn-dock",
        site_id: "site-soft-1",
        device_id: "CS-5512",
        ll: [3.1551, 101.7142],
      },
      evidenceRows: [
        evidence("ss-6", "SOFT·D1", "ai", "schedule", now - 120000, "dock"),
      ],
      aiActions: [
        aiAct("d1", "schedule", "Night window 00:00–06:00 Soft open", now - 130000, "armed"),
        aiAct("d2", "open_device_cam", "Opened dock cam CS-5512", now - 120000, "soft_preview"),
      ],
      pulseAgoMs: 20000,
    }),
  ];

  const dispatchCards = [
    card({
      source_id: "soft:dispatch:r12",
      label: "Responder · field lens",
      lane: "dispatch",
      tone: "field",
      why: "Dispatch media session soft-projected for cinema rehearsal.",
      control: { kind: "ai", label: "Dispatch AI" },
      selection: "Active dispatch assignee media · Soft demo ticketless.",
      dispatch: { id: "DSP-SOFT-441", state: "en_route", assignee: "Unit R-12" },
      loop: sharedLoop,
      nearbyRows: [
        nearby("soft:device:gate-a", "North Gate · Cam A", 110, "Approach target"),
        nearby("p-soft-1", "Responder Mei", 0, "Self"),
      ],
      response: { summary: "Phone uplink Soft-only", posture: "en_route" },
      swarm: { active: true, linked: 6, group_id: "grp-gate" },
      place: {
        address: "Approaching North Gate · ETA soft 4m",
        zone_id: "zn-north",
        site_id: "site-soft-1",
        device_id: "",
        ll: [3.1576, 101.7119],
      },
      evidenceRows: [
        evidence("ss-4", "SOFT·R1", "ai", "dispatch_clip", now - 60000, "field", "recording", {
          ai_read: "Field clip while en route · ETA hold.",
          ai_fixed: true,
        }),
        evidence("ss-5", "SOFT·R2", "human", "personnel_upload", now - 15000, "field", "upload", {
          author: "Responder Mei",
          author_role: "responder",
          note: "ETA 4m · corridor clear · need Gate A overlap.",
          ai_read: "Reason noted · FOV overlap request queued.",
          ai_fixed: true,
        }),
        evidence("ss-5b", "SOFT·R3", "ai", "approach_snap", now - 8000, "field", "snapshot", {
          ai_read: "Approach snap sealed after note.",
          ai_fixed: true,
        }),
      ],
      aiActions: [
        aiAct("r1", "open_personnel_cam", "Opened responder field lens for Unit R-12", now - 65000, "soft_preview"),
        aiAct("r2", "track_eta", "ETA soft 4m toward North Gate", now - 40000, "updating"),
        aiAct("r3", "link_device", "Linked device Gate A into same dispatch loop", now - 35000, "linked"),
        aiAct("r4", "loop_update", "Result loop: improving · awaiting on-scene", now - 10000, "improving"),
      ],
      pulseAgoMs: 4000,
    }),
  ];

  const all = [...deviceCards, ...dispatchCards];
  const activity = [];
  for (const c of all) {
    const sit = c.situation;
    activity.push({
      id: "lens:" + c.source_id,
      kind: "lens",
      tone: "idle",
      title: c.label,
      detail: sit.response.summary || sit.why,
      control: sit.control.kind,
      actor: sit.control.label,
      address: sit.place.address,
      source_id: c.source_id,
      lane: c.lane,
      at: c.pulse_at,
    });
    if (sit.selection) {
      activity.push({
        id: "team:" + c.source_id,
        kind: "team",
        title: "Agent team discuss",
        detail: sit.selection,
        actor: "agent team",
        source_id: c.source_id,
        at: (c.pulse_at || 0) - 2,
      });
    }
    if (sit.why) {
      activity.push({
        id: "think:" + c.source_id,
        kind: "think",
        title: "AI thinking",
        detail: sit.why,
        actor: sit.control && sit.control.label,
        source_id: c.source_id,
        at: (c.pulse_at || 0) - 1,
      });
    }
  }
  activity.sort((a, b) => (b.at || 0) - (a.at || 0));

  return {
    device_cards: deviceCards,
    dispatch_cards: dispatchCards,
    activity: activity.slice(0, 40),
    count: all.length,
    generated_at: new Date(now).toISOString(),
    honesty: "soft_demo_fixture",
    soft_demo: true,
    soft_stamp: true,
    soft_neq_live: true,
    soft_reason: "soft_demo_fixture",
    live_authority: false,
  };
}

export function shouldInjectSoftCinemaDemo(wallPayload) {
  if (!isLocalHost()) return false;
  try {
    const q = new URLSearchParams(location.hash.split("?")[1] || "");
    if (q.get("live_only") === "1") return false;
    if (q.get("soft") === "1") return true;
  } catch {
    /* ignore */
  }
  const devices = Array.isArray(wallPayload?.device_cards) ? wallPayload.device_cards : [];
  const dispatches = Array.isArray(wallPayload?.dispatch_cards) ? wallPayload.dispatch_cards : [];
  return devices.length + dispatches.length === 0;
}

export function mergeSoftCinemaDemo(wallPayload) {
  if (!shouldInjectSoftCinemaDemo(wallPayload)) {
    return wallPayload || { device_cards: [], dispatch_cards: [], activity: [] };
  }
  return buildSoftCinemaDemoWall();
}
